import { Batch, PipelineStepEnumSchema, StatusEnumSchema } from "@domain/schemas/BI";
import { DropdownFilterCell } from "@presentation/components/DropdownFilterCell";
import { process } from "@progress/kendo-data-query";
import {
  GridDetailRowProps,
  GRID_PREVENT_SELECTION_ELEMENT,
  GridDataStateChangeEvent,
  getSelectedState,
  GridSelectionChangeEvent,
  GridFilterCell,
  GridFilterCellProps,
  GridColumn,
  Grid,
  GridExpandChangeEvent,
} from "@progress/kendo-react-grid";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { DATA_ITEM_KEY, EXPANDED_FIELD, SELECTED_FIELD } from "@presentation/components/helpers/constants";
import { useInjection } from "@presentation/components/dependencyInjection";
import { DownloadResultsService, ListJobsService } from "@application/useCases";
import { BatchArtifactsViewerContext, DropdownFilterFocusContext } from "../JobList";
import { BatchListItemState, DetailDataGridStore } from "@presentation/common";
import { SamplesView } from "./SamplesView";
import { createDetailDataGridStore } from "@presentation/common/stores/DetailDataGridStore";
import { useTranslation } from "react-i18next";
import "@presentation/components/job/styles/progressBarStyle.scss";
import StatusCell from "./StatusCell";
import ShowLogsButton from "./ShowLogsButton";
import { Button } from "@progress/kendo-react-buttons";
import { subscribeToJob, subscribeToProgress, subscribeToRefreshJob } from "@infrastructure/realtime/jobsHubClient";
import { downloadIcon, fileIcon } from "@progress/kendo-svg-icons";
import { useArtifactDownloadDialog } from "./useArtifactDownloadDialog";
import { useShallow } from "zustand/react/shallow";

const useBatchGridStore = createDetailDataGridStore<BatchListItemState, Batch>();

const stopPropagation = (event: React.SyntheticEvent<HTMLElement>) => {
  event.stopPropagation();
};

const TextFilterCell = (properties: GridFilterCellProps) => {
  const { isDropdownFilterFocusedRef } = useContext(DropdownFilterFocusContext);

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }

    isDropdownFilterFocusedRef.current = false;
  };

  return (
    <div
      {...{ [GRID_PREVENT_SELECTION_ELEMENT]: true }}
      onFocus={() => {
        isDropdownFilterFocusedRef.current = true;
      }}
      onBlur={handleBlur}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
    >
      <GridFilterCell {...properties} />
    </div>
  );
};

export const BatchesView = (properties: GridDetailRowProps) => {
  const { isDropdownFilterFocusedRef, showFilters } = useContext(DropdownFilterFocusContext);
  const { openLogs, openResults } = useContext(BatchArtifactsViewerContext);

  const jobId: string = properties.dataItem.jobId;
  const jobPipelineStepId: string = properties.dataItem.id;
  const listJobTasksService = useInjection(ListJobsService);
  const downloadResultsService = useInjection(DownloadResultsService);

  const { detailList, metaState, selectedState, setDetailList, setIsExpanded, setMetaStateForDetail, setSelectedStateForDetail, setProgress } = useBatchGridStore(
    useShallow((state: DetailDataGridStore<BatchListItemState, Batch>) => ({
      detailList: state.detailDictionary[jobPipelineStepId]?.detailList,
      metaState: state.detailDictionary[jobPipelineStepId]?.metaState,
      selectedState: state.detailDictionary[jobPipelineStepId]?.selectedState,
      setDetailList: state.setDetailList,
      setIsExpanded: state.setIsExpanded,
      setMetaStateForDetail: state.setMetaStateForDetail,
      setSelectedStateForDetail: state.setSelectedStateForDetail,
      setProgress: state.setProgress,
    })),
  );

  const isDisplayFilter = detailList?.length > 1;
  const [isLoading, setIsLoading] = useState<boolean>(!detailList || detailList.length === 0);
  const { t: loc } = useTranslation();
  const processedDetailList = useMemo(() => process(detailList ?? [], metaState ?? {}), [detailList, metaState]);

  const { artifactDownloadDialog, openBatchDownloadDialog } = useArtifactDownloadDialog({ downloadResultsService });

  async function handleBatchDownload(batchId: string) {
    try {
      await openBatchDownloadDialog(jobId, jobPipelineStepId, batchId);
    } catch {
      alert("Failed to download batch artifacts.");
    }
  }

  useEffect(() => {
    const fetchBatchList = async () => {
      if (isDropdownFilterFocusedRef.current) {
        return null;
      }

      try {
        const taskBatchListResponse = await listJobTasksService.getJobStepBatchListAsync(jobId, jobPipelineStepId);

        const taskBatchList = (taskBatchListResponse.list || []).map((jobTask) => ({
          ...jobTask,
          fileNames: jobTask.rawFileNames?.join(", ") || "",
          virtualCreatedAt: jobTask.createdAt ? new Date(jobTask.createdAt) : jobTask.createdAt,
          virtualStartedAt: jobTask.startedAt ? new Date(jobTask.startedAt) : jobTask.startedAt,
          virtualFinishedAt: jobTask.finishedAt ? new Date(jobTask.finishedAt) : jobTask.finishedAt,
          task: jobTask.id,
          jobId,
          progress: jobTask.progress,
        }));

        setDetailList(jobPipelineStepId, taskBatchList);
        return taskBatchList;
      } catch (error) {
        console.error("Error fetching batch list:", error);
        return null;
      } finally {
        setIsLoading(false);
      }
    };

    void fetchBatchList();

    const unsubscribeFromJob = subscribeToJob(jobId);
    const unsubscribeFromRefreshJob = subscribeToRefreshJob((refreshedJobId) => {
      if (refreshedJobId === jobId && !isDropdownFilterFocusedRef.current) {
        void fetchBatchList();
      }
    });
    const unsubscribeFromProgress = subscribeToProgress((data) => {
      if (data.jobId === jobId) {
        setProgress(jobPipelineStepId, data.batchId, data.progress);
      }
    });

    return () => {
      unsubscribeFromProgress();
      unsubscribeFromRefreshJob();
      unsubscribeFromJob();
    };
  }, [jobId, jobPipelineStepId]);

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setSelectedStateForDetail(jobPipelineStepId, newSelectedState);
  };

  const handleDataStateChange = (e: GridDataStateChangeEvent) => {
    const dataState = { ...e.dataState };
    if (dataState.sort) {
      const statusSort = dataState.sort.find((s) => s.field === "status");
      if (statusSort) {
        const otherSorts = dataState.sort.filter((s) => s.field !== "progress");
        const progressDir = statusSort.dir === "asc" ? "desc" : "asc";
        dataState.sort = [...otherSorts, { field: "progress", dir: progressDir }];
      }
    }
    setMetaStateForDetail(jobPipelineStepId, dataState);
  };

  const handleExpandChange = (event: GridExpandChangeEvent) => {
    event.dataItem.expanded = event.value;

    if (!event.dataItem.id) return;
    setIsExpanded(jobPipelineStepId, event.dataItem.id, event.value);
  };

  if (detailList && !isLoading) {
    return (
      <div {...{ [GRID_PREVENT_SELECTION_ELEMENT]: true }}>
        {artifactDownloadDialog}
        <Grid
          style={{ maxHeight: "40vh", width: "100%" }}
          dataItemKey={DATA_ITEM_KEY}
          selectedField={SELECTED_FIELD}
          expandField={EXPANDED_FIELD}
          scrollable="scrollable"
          resizable={true}
          sortable={true}
          filterable={showFilters && isDisplayFilter}
          navigatable={false}
          selectable={{
            enabled: true,
            drag: false,
            mode: "multiple",
          }}
          {...metaState}
          data={processedDetailList}
          detail={SamplesView}
          onExpandChange={handleExpandChange}
          onDataStateChange={handleDataStateChange}
          onSelectionChange={handleSelectionChange}
        >
          <GridColumn
            field="status"
            title={loc("status")}
            width="220px"
            minResizableWidth={100}
            cell={(properties_) => <StatusCell status={properties_.dataItem.status} progress={properties_.dataItem.progress} isbatch={true} currentStep={properties_.dataItem.name} />}
            filterCell={(properties_) => (
              <DropdownFilterCell
                {...properties_}
                data={Object.values(StatusEnumSchema) as string[]}
                defaultItem={"Select status"}
                onToggle={(isFocused) => (isDropdownFilterFocusedRef.current = isFocused)}
              />
            )}
          />
          <GridColumn
            title={loc("logs")}
            width="140px"
            minResizableWidth={120}
            filterable={false}
            cell={(properties_) => (
              <td style={{ position: "relative", overflow: "visible" }}>
                {![StatusEnumSchema.Created, StatusEnumSchema.Pending, StatusEnumSchema.Scheduled].includes(properties_.dataItem.status) && (
                  <ShowLogsButton
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      void openLogs(jobId, jobPipelineStepId, { id: properties_.dataItem.id, status: properties_.dataItem.status });
                    }}
                    aria-label="Show Logs"
                  />
                )}
              </td>
            )}
          />
          {[PipelineStepEnumSchema.PulsarStep1, PipelineStepEnumSchema.PulsarStep3, PipelineStepEnumSchema.DiaAnalysis].includes(properties.dataItem.type) && (
            <GridColumn field="fileNames" title={"File"} width="500px" filterCell={TextFilterCell} />
          )}
          <GridColumn
            title={"Artifacts"}
            width="150px"
            minResizableWidth={130}
            filterable={false}
            cell={(properties) => (
              <td style={{ textAlign: "left" }}>
                {properties.dataItem.status === StatusEnumSchema.Completed && (
                  <Button
                    themeColor="secondary"
                    fillMode="link"
                    style={{ padding: 0 }}
                    svgIcon={downloadIcon}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleBatchDownload(properties.dataItem.id);
                    }}
                  >
                    Download
                  </Button>
                )}
              </td>
            )}
          />
          <GridColumn
            title={"Results Overview"}
            width="150%"
            minResizableWidth={220}
            filterable={false}
            cell={(properties) => (
              <td style={{ textAlign: "left" }}>
                {properties.dataItem.status === StatusEnumSchema.Completed && (
                  <Button
                    themeColor="secondary"
                    fillMode="link"
                    style={{ padding: 0 }}
                    svgIcon={fileIcon}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      void openResults(jobId, jobPipelineStepId, { id: properties.dataItem.id, status: properties.dataItem.status });
                    }}
                  >
                    {properties.dataItem.precursorsCount ?? "Results"}
                  </Button>
                )}
              </td>
            )}
          />
          <GridColumn field="virtualStartedAt" title={loc("startedAt")} width="280px" filter="date" format="{0:dd/MM/yyyy h:mm a}" />
          <GridColumn field="virtualFinishedAt" title={loc("finishedAtNoTime")} width="280" minResizableWidth={220} filter="date" format="{0:dd/MM/yyyy h:mm a}" />
          <GridColumn field="id" title="ID" width="290px" minResizableWidth={220} filterCell={TextFilterCell} />
          <GridColumn width="auto" minResizableWidth={0} filterable={false} />
        </Grid>
      </div>
    );
  }

  return (
    <div style={{ height: "50px", width: "100%" }}>
      <div style={{ position: "absolute", width: "100%" }}>
        <div className="k-loading-image" />
      </div>
    </div>
  );
};
